<script setup lang="ts">
import type { SuggestedAction } from '@acepoker/contract'
import { GameState, useGameState } from '../../stores/gameState'
import { storeToRefs } from 'pinia'
import { SuggestedActionQuality } from '@acepoker/api-core'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps<{
  actions: SuggestedAction[]
  lastActionNameRaw?: string
}>()

type SuggestedActionExtended = SuggestedAction & {
  label: string
  suffix?: string
}

const { gameState } = storeToRefs(useGameState())

const prepareSuggestedActionData = (suggestedActions: SuggestedAction[]) => {
  const sorted = [...suggestedActions].sort(({ actionNameRaw: a }, { actionNameRaw: b }) => {
    if (a === 'c' && b === 'f') {
      return -1
    }
    if (a === 'f' && b === 'c') {
      return 1
    }
    if (a === 'c') {
      return -1
    }
    if (a === 'f') {
      return -1
    }
    if (a.startsWith('allin')) {
      return 1
    }
    if (b.startsWith('allin')) {
      return -1
    }
    return 0
  })
  const result: SuggestedActionExtended[] = []
  for (const action of sorted) {
    const { actionNameRaw, amount } = action
    if (actionNameRaw === 'c') {
      result.push({
        ...action,
        label: 'label.Call'
      })
    }
    if (actionNameRaw === 'f') {
      result.push({
        ...action,
        label: 'label.Fold'
      })
    }
    if (actionNameRaw === 'x') {
      result.push({
        ...action,
        label: 'label.Check'
      })
    }
    if (actionNameRaw.startsWith('allin') && amount) {
      result.push({
        ...action,
        label: 'label.AllIn'
      })
    }
    if (actionNameRaw.startsWith('r') && amount) {
      result.push({
        ...action,
        label: 'label.Raise',
        suffix: `${actionNameRaw.replace(/\D/g, '')}%`
      })
    }
    if (actionNameRaw.startsWith('b') && amount) {
      result.push({
        ...action,
        label: 'label.Bet',
        suffix: `${actionNameRaw.replace(/\D/g, '')}%`
      })
    }
  }
  return result
}
</script>

<template>
  <div
    :class="{
      'history-suggested-actions': true,
      'history-suggested-actions--away':
        gameState === GameState.ERROR ||
        gameState === GameState.DISCONNECTED ||
        gameState === GameState.INIT,
      'history-suggested-actions__1': props.actions.length == 1,
      'history-suggested-actions__2': props.actions.length == 2
    }"
  >
    <div
      :key="action.actionNameRaw"
      v-for="action of prepareSuggestedActionData(props.actions)"
      :class="[
        'history-suggested-actions-button',
        {
          'history-suggested-actions-button__selected':
            props.lastActionNameRaw == action.actionNameRaw,
          'history-suggested-actions-button__unselected':
            props.lastActionNameRaw !== action.actionNameRaw,
          'history-suggested-actions-button__best': action.quality === SuggestedActionQuality.Best
        }
      ]"
    >
      <div class="history-suggested-actions-button-box">
        <div
          :class="[
            'history-suggested-actions-button-label',
            'history-suggested-actions-button-label__review-size',
            {
              'history-suggested-actions-button-label__review-color':
                props.lastActionNameRaw !== action.actionNameRaw &&
                action.quality !== SuggestedActionQuality.Best
            }
          ]"
        >
          {{ t(action.label) }} {{ action.suffix }}
        </div>
        <transition name="history-suggested-actions-button-data-box">
          <div class="history-suggested-actions-button-data-box">
            <div class="history-suggested-actions-button-strategy">
              {{ (action.strategy * 100).toFixed(1) }} %
            </div>
            <div class="history-suggested-actions-button-ev">
              {{ action.ev.toFixed(1) }}
              {{ $t('SuggestedActions.label.EV') }}
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.history-suggested-actions {
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  border-radius: 8px;
  overflow: hidden;

  &__1 {
    grid-template-columns: 1fr;
  }

  &__2 {
    grid-template-columns: 1fr 1fr;
  }

  &--away {
    filter: grayscale(100%);
  }

  &-button {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    transition: all 0.2s ease-out;

    &__unselected {
      background: #303037;
    }

    &__selected {
      background: #a93939 !important;
    }

    &__best {
      background: #279a33 !important;
    }

    &-box {
      display: flex;
      flex-direction: column;
      transition: all 0.2s ease-out;
    }

    &-label {
      text-align: center;
      font-family: Poppins;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      color: white;
      text-shadow: 0px 1px 0px rgba(0, 0, 0, 0.4);
      transition: all 0.2s ease-out;

      &__review-size {
        font-size: 10px;
      }

      &__review-color {
        color: #b5b4ba;
      }
    }

    &-data-box {
      display: flex;
      gap: 8px;
      justify-content: space-between;
      color: #fff;
      text-align: center;
      font-family: Poppins;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;

      &-enter-active,
      &-leave-active {
        transition: all 0.2s ease-out;
      }

      &-enter-from,
      &-leave-to {
        max-height: 0;
        opacity: 0;
      }
    }
  }
}

</style>
